import React, { memo, useState, useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Card, CardContent, Typography, Avatar, Box } from '@material-ui/core'
import { Rating } from '@material-ui/lab'

import styles from '../styles'
import { combineStyles, PlaceholderContent } from 'utils'
import { IPublicCompany } from 'models'
import { Loading } from 'components'
import commonStyles from '../../../styles/common'
import { useCompanyQuery } from 'graphql/__generated__/graphql'
import { useApolloClient } from '@apollo/react-hooks'
import { PublicCompanyQuery } from 'store'

const CompanyTestimonials: React.FunctionComponent<any> = () => {
	const [publicCompany, setPublicCompany] = useState<IPublicCompany>()
	const client = useApolloClient()
	const { companySlug }: { companySlug?: string } = useParams()
	const combinedStyles: any = combineStyles(commonStyles, styles)
	const useStyles = makeStyles(combinedStyles)
	const classes = useStyles()
	const { t } = useTranslation()

	const { data: companyData } = useCompanyQuery({
		variables: {
			slug: '%' + companySlug + '%',
		},
	})

	const company = useMemo(
		() =>
			(companyData &&
				companyData?.companyCollection?.edges?.map(x => x.node))?.[0] ||
			null,
		[companyData],
	)

	useEffect(() => {
		if (company?.name)
			client
				.query({
					query: PublicCompanyQuery,
					variables: {
						name: company?.name,
					},
				})
				.then((companyResponse: any) => {
					setPublicCompany(
						companyResponse.data.public_companiesCollection.edges[0]
							.node,
					)
				})
	}, [client, company])

	const testimonials =
		publicCompany?.company_testimonialsCollection?.edges?.map(
			(edge: any) => edge.node,
		) || []

	return (
		<Grid
			className={classes.companyMainContent}
			container={true}
			direction="column"
		>
			<Box mb={3}>
				<Typography variant="h4" component="h2" gutterBottom>
					{t('Testimonials')}
				</Typography>
			</Box>

			{publicCompany === undefined ? (
				<Loading size={48} style={{ margin: '100px auto' }} />
			) : (
				<Grid container={true} spacing={3}>
					{testimonials && testimonials.length > 0 ? (
						testimonials.map((testimonial: any, index: number) => (
							<Grid
								item
								xs={12}
								md={6}
								key={testimonial.id || index}
							>
								<Card
									elevation={2}
									style={{
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<CardContent style={{ flex: 1 }}>
										<Box
											display="flex"
											alignItems="center"
											mb={2}
										>
											<Avatar
												src={
													testimonial.original_avatar_path
														? `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${testimonial.original_avatar_path}`
														: undefined
												}
												alt={testimonial.name}
												style={{
													width: 60,
													height: 60,
													marginRight: 16,
												}}
											/>
											<Box>
												<Typography
													variant="h6"
													component="h3"
												>
													{testimonial.name}
												</Typography>
												<Typography
													variant="body2"
													color="textSecondary"
												>
													{testimonial.role}
												</Typography>
												{testimonial.stars && (
													<Rating
														value={
															testimonial.stars
														}
														readOnly
														size="small"
													/>
												)}
											</Box>
										</Box>
										<Typography
											variant="body1"
											component="p"
											style={{ marginBottom: 16 }}
										>
											{testimonial.review}
										</Typography>
										{testimonial.started_at && (
											<Typography
												variant="caption"
												color="textSecondary"
											>
												{t('Started')}:{' '}
												{new Date(
													testimonial.started_at,
												).toLocaleDateString()}
											</Typography>
										)}
									</CardContent>
								</Card>
							</Grid>
						))
					) : (
						<Grid item xs={12}>
							<PlaceholderContent
								data={publicCompany?.name || t('the company')}
								entity={'testimonials'}
							/>
						</Grid>
					)}
				</Grid>
			)}
		</Grid>
	)
}

export default memo(CompanyTestimonials)
